import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Subject {
  id: string;
  name: string;
  description: string;
  code: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  gradeId: string;
  gradeName: string;
  teacherCount: number;
  courseCount: number;
}

interface Teacher {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  active: boolean;
  hireDate: string;
  yearsOfExperience: number;
  biography: string;
  imagePath: string | null;
}

interface Course {
  id: string;
  courseName: string;
  term: string;
  active: boolean;
  price: number;
  description: string;
  imagePath: string | null;
  createdDate: string;
  modificationDate: string;
  isOpenToAll: boolean;
  subjectId: string;
  subjectName: string;
  subjectCode: string | null;
  teacherId: string;
  teacherName: string;
  teacherEmail: string | null;
  gradeName: string | null;
}

interface Grade {
  id: string;
  name: string;
  description: string;
  level: number;
  code: string;
  roleName: string;
  active: boolean;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
}

const SubjectDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('details');
  const [subject, setSubject] = useState<Subject | null>(null);
  const [grades, setGrades] = useState<Grade[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editedSubject, setEditedSubject] = useState<Subject | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  
  // Teachers tab state
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingTeachers, setLoadingTeachers] = useState(false);
  const [teachersError, setTeachersError] = useState<string | null>(null);
  const [showAssignTeacherModal, setShowAssignTeacherModal] = useState(false);
  const [availableTeachers, setAvailableTeachers] = useState<Teacher[]>([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [assigningTeacher, setAssigningTeacher] = useState(false);

  // Courses tab state
  const [courses, setCourses] = useState<Course[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [coursesError, setCoursesError] = useState<string | null>(null);

  // Fetch subject details
  const fetchSubject = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl(`Subject/${id}`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSubject(data.data);
        setEditedSubject(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch subject details');
      }
    } catch (error) {
      console.error('Error fetching subject:', error);
      setError('Failed to load subject details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch grades for dropdown
  const fetchGrades = async () => {
    try {
      const response = await authFetch(apiUrl('Grade'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setGrades(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch grades');
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
      // We don't set the main error state here as it's not critical
    }
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value } = e.target;
    if (editedSubject) {
      setEditedSubject({
        ...editedSubject,
        [name]: value
      });
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!editedSubject || !id) return;

    setSaving(true);
    setSaveError(null);

    try {
      const response = await authFetch(apiUrl(`Subject/${id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          id: editedSubject.id,
          name: editedSubject.name,
          description: editedSubject.description,
          code: editedSubject.code,
          gradeId: editedSubject.gradeId,
          active: editedSubject.active
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubject(data.data);
        setEditedSubject(data.data);
        alert('تم حفظ التغييرات بنجاح');
      } else {
        throw new Error(data.message || 'Failed to update subject');
      }
    } catch (error) {
      console.error('Error updating subject:', error);
      setSaveError('Failed to save changes. Please try again later.');
    } finally {
      setSaving(false);
    }
  };

  // Fetch available teachers for assignment
  const fetchAvailableTeachers = async () => {
    try {
      const response = await authFetch(apiUrl('Teacher'), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setAvailableTeachers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch available teachers');
      }
    } catch (error) {
      console.error('Error fetching available teachers:', error);
      alert('Failed to load available teachers. Please try again later.');
    }
  };

  // Handle teacher assignment
  const handleAssignTeacher = async () => {
    if (!selectedTeacherId || !id) return;

    setAssigningTeacher(true);
    try {
      const response = await authFetch(
        apiUrl(`Teacher/${selectedTeacherId}/assign-subject/${id}`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          },
          body: JSON.stringify({
            teacherId: selectedTeacherId,
            subjectId: id,
            notes: '',
            active: true
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        setShowAssignTeacherModal(false);
        setSelectedTeacherId('');
        fetchTeachers(); // Refresh the teachers list
        alert('تم تعيين المعلم بنجاح');
      } else {
        throw new Error(data.message || 'Failed to assign teacher');
      }
    } catch (error) {
      console.error('Error assigning teacher:', error);
      alert('Failed to assign teacher. Please try again later.');
    } finally {
      setAssigningTeacher(false);
    }
  };

  // Fetch teachers
  const fetchTeachers = async () => {
    if (!id) return;
    setLoadingTeachers(true);
    setTeachersError(null);

    try {
      const response = await authFetch(apiUrl(`subject/${id}/teachers`), {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setTeachers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch teachers');
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      setTeachersError('Failed to load teachers. Please try again later.');
      setTeachers([]);
    } finally {
      setLoadingTeachers(false);
    }
  };

  // Fetch courses
  const fetchCourses = async () => {
    if (!id) return;
    setLoadingCourses(true);
    setCoursesError(null);

    try {
      const response = await authFetch(
        apiUrl(`Subject/${id}/courses`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        setCourses(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setCoursesError('Failed to load courses. Please try again later.');
      setCourses([]);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchSubject();
    fetchGrades();
  }, [id]);

  // Fetch teachers when tab changes to teachers
  useEffect(() => {
    if (activeTab === 'teachers') {
      fetchTeachers();
    }
  }, [activeTab, id]);

  // Fetch courses when tab changes to courses or page changes
  useEffect(() => {
    if (activeTab === 'courses') {
      fetchCourses();
    }
  }, [activeTab, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center" dir="rtl">
        <div className="text-red-600 text-center">
          <p className="text-xl font-semibold mb-2">حدث خطأ</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
            تفاصيل المادة
          </h1>
          <p className="mt-2 text-gray-600">{subject?.name}</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-reverse space-x-4 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
                activeTab === 'details'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              التفاصيل
            </button>
            <button
              onClick={() => setActiveTab('teachers')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
                activeTab === 'teachers'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              المعلمين
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors duration-200 ${
                activeTab === 'courses'
                  ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              الدورات
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          {/* Details Tab */}
          {activeTab === 'details' && editedSubject && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    اسم المادة
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={editedSubject.name}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
                    الكود
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={editedSubject.code}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="gradeId" className="block text-sm font-medium text-gray-700 mb-1">
                    السنة الدراسية
                  </label>
                  <select
                    id="gradeId"
                    name="gradeId"
                    value={editedSubject.gradeId}
                    onChange={handleInputChange}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {grades.map((grade) => (
                      <option key={grade.id} value={grade.id}>
                        {grade.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="active" className="block text-sm font-medium text-gray-700 mb-1">
                    الحالة
                  </label>
                  <select
                    id="active"
                    name="active"
                    value={editedSubject.active.toString()}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setEditedSubject({ ...editedSubject, active: e.target.value === 'true' })}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="true">نشط</option>
                    <option value="false">غير نشط</option>
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    الوصف
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={editedSubject.description}
                    onChange={handleInputChange}
                    rows={4}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {saveError && (
                <div className="text-red-600 text-sm mt-4">
                  {saveError}
                </div>
              )}

              <div className="flex justify-end mt-6">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحفظ...
                    </>
                  ) : (
                    'حفظ التغييرات'
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Teachers Tab */}
          {activeTab === 'teachers' && (
            <div>
              <div className="flex justify-end mb-6">
                <button
                  onClick={() => {
                    setShowAssignTeacherModal(true);
                    fetchAvailableTeachers();
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  إضافة معلم
                </button>
              </div>

              {loadingTeachers ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : teachersError ? (
                <div className="text-red-600 text-center py-12">
                  {teachersError}
                </div>
              ) : teachers.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">لا يوجد معلمين لهذه المادة</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          اسم المعلم
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          البريد الإلكتروني
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          رقم الهاتف
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          الحالة
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {teachers.map((teacher) => (
                        <tr key={teacher.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {teacher.firstName} {teacher.lastName}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{teacher.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-500">{teacher.phoneNumber}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                              ${teacher.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                            >
                              {teacher.active ? 'نشط' : 'غير نشط'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Courses Tab */}
          {activeTab === 'courses' && (
            <div>
              {loadingCourses ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : coursesError ? (
                <div className="text-red-600 text-center py-12">
                  {coursesError}
                </div>
              ) : courses.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">لا يوجد دورات لهذه المادة</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr className="bg-gray-50">
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            اسم الدورة
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            اسم المعلم
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            الفصل الدراسي
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            الحالة
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            تاريخ التعديل
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {courses.map((course) => (
                          <tr key={course.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{course.courseName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{course.teacherName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">{course.term}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                ${course.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                              >
                                {course.active ? 'نشط' : 'غير نشط'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-500">
                                {new Date(course.modificationDate).toLocaleDateString('ar-EG')}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                </>
              )}
            </div>
          )}

          {/* Assign Teacher Modal */}
          {showAssignTeacherModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-200 transform transition-all">
                <div className="text-center mb-5">
                  <h3 className="text-xl font-medium text-gray-900 mb-2">إضافة معلم للمادة</h3>
                  <p className="text-gray-600">
                    اختر المعلم الذي تريد تعيينه لمادة <span className="font-bold text-gray-900">{subject?.name}</span>
                  </p>
                </div>

                <div className="mt-4">
                  <label htmlFor="teacherId" className="block text-sm font-medium text-gray-700 mb-1">
                    اختر المعلم
                  </label>
                  <select
                    id="teacherId"
                    value={selectedTeacherId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedTeacherId(e.target.value)}
                    className="block w-full px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">اختر معلم</option>
                    {availableTeachers.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.firstName} {teacher.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                    onClick={() => {
                      setShowAssignTeacherModal(false);
                      setSelectedTeacherId('');
                    }}
                    disabled={assigningTeacher}
                  >
                    إلغاء
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 flex items-center gap-2"
                    onClick={handleAssignTeacher}
                    disabled={!selectedTeacherId || assigningTeacher}
                  >
                    {assigningTeacher ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        جاري التعيين...
                      </>
                    ) : (
                      'تعيين المعلم'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubjectDetails;
